import { type FunctionComponent, memo } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useMirrorTrackPageLoad } from "shared/components/tracking/mirror/hooks";
import { useRefreshToken } from "shared/lib/account";
import { useAccountSDKSetup, useLoginCleanupEffect } from "shared/lib/account/hooks";

import { usePageTargeting } from "scmp-app/components/advertisement/google-publisher-tag";
import { useAdvertisementViewabilitySetup } from "scmp-app/components/advertisement/viewability-tracking";
import { useArticleCommentsActionRedirect } from "scmp-app/components/article/article-comments-drawer/article-comments-action-redirect/hooks";
import { useAppPromotionSnackbar } from "scmp-app/components/common/snackbar/app-promotion-item/hooks";
import { useContentBookmarkActionRedirect } from "scmp-app/components/content/content-bookmark/content-bookmark-action-redirect/hooks";
import { useFollowActionRedirect } from "scmp-app/components/follow-button/follow-action-redirect/hooks";
import { useFollowConfigActionRedirect } from "scmp-app/components/follow-button/follow-config-action-redirect/hooks";
import { useBookmarkRippleEffectSetup } from "scmp-app/components/header/header-bookmark/hooks";
import { useTrackingSetup } from "scmp-app/components/tracking";
import { useSetGA4UserProperties } from "scmp-app/components/tracking/google-analytics-4/user-properties";
import { useAppSetup } from "scmp-app/lib/app/hooks";
import { useLocalStorageCleanUp } from "scmp-app/lib/hooks/local-storage-clean-up";
import { useRosettaSetup } from "scmp-app/lib/rosetta";
import { useRosettaPianoTemplateTracking } from "scmp-app/lib/rosetta/hooks/rosetta-piano-template-tracking";
import { useUserServiceInit } from "scmp-app/lib/user-service/hooks";

import { ErrorHandler } from "./error-handler";
import { HookRunner } from "./hook-runner";

/**
 * This component is using for running the hooks that no need to be prioritized
 */
export const Component: FunctionComponent = () => (
  <>
    {[
      useAdvertisementViewabilitySetup,
      useAppSetup,
      useBookmarkRippleEffectSetup,
      useRosettaSetup,
      useRosettaPianoTemplateTracking,
      useTrackingSetup,
      usePageTargeting,
      useArticleCommentsActionRedirect,
      useContentBookmarkActionRedirect,
      useFollowActionRedirect,
      useMirrorTrackPageLoad,
      useFollowConfigActionRedirect,
      useAccountSDKSetup,
      useRefreshToken,
      useLoginCleanupEffect,
      useAppPromotionSnackbar,
      useLocalStorageCleanUp,
      useSetGA4UserProperties,
      useUserServiceInit,
    ].map((hook, index) => (
      <ErrorBoundary FallbackComponent={ErrorHandler} key={index}>
        <HookRunner hook={hook} />
      </ErrorBoundary>
    ))}
  </>
);
Component.displayName = "SimpleHooksRunner";

export const SimpleHooksRunner = memo(Component);
